import type { Scene } from '@babylonjs/core/scene';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { LinesMesh, InstancedLinesMesh } from '@babylonjs/core/Meshes/linesMesh';
import { CreateLineSystem } from '@babylonjs/core/Meshes/Builders/linesBuilder';
import type { DebugConfig } from '../config/debug.config';
import { trackFrame, type TrackManager } from '../level/TrackManager';
import type { Aabb } from '../physics/Aabb';

type Category = 'player' | 'obstacle' | 'collectible' | 'terrain' | 'hazard';

const DEBUG_RENDERING_GROUP = 1;

/**
 * Immediate-mode wireframe boxes for gameplay colliders (track-space AABBs).
 * Uses pooled instances of one unit wire cube per color – cheap enough to
 * redraw every frame.
 */
export class ColliderDebugRenderer {
  private readonly sources: Record<Category, LinesMesh>;
  private readonly pools: Record<Category, InstancedLinesMesh[]> = { player: [], obstacle: [], collectible: [], terrain: [], hazard: [] };
  private readonly used: Record<Category, number> = { player: 0, obstacle: 0, collectible: 0, terrain: 0, hazard: 0 };
  private readonly frame = trackFrame();

  constructor(
    scene: Scene,
    private readonly track: TrackManager,
    config: DebugConfig,
  ) {
    const color = (hex: string, alpha = 1) => Color3.FromHexString(hex).toColor4(alpha);
    this.sources = {
      player: unitCube(scene, 'DEBUG_COLLIDER_PLAYER', color(config.colors.player)),
      obstacle: unitCube(scene, 'DEBUG_COLLIDER_OBSTACLE', color(config.colors.colliders)),
      collectible: unitCube(scene, 'DEBUG_COLLIDER_COLLECTIBLE', color(config.colors.collectibleSpawnPoints, 0.6)),
      terrain: unitCube(scene, 'DEBUG_COLLIDER_TERRAIN', color(config.colors.terrain)),
      hazard: unitCube(scene, 'DEBUG_COLLIDER_HAZARD', color(config.colors.hazard)),
    };
  }

  begin(): void {
    this.used.player = this.used.obstacle = this.used.collectible = this.used.terrain = this.used.hazard = 0;
  }

  draw(box: Readonly<Aabb>, category: Category): void {
    const midZ = (box.minZ + box.maxZ) / 2;
    if (!this.track.segmentAt(midZ)) return;
    const f = this.track.trackToWorld((box.minX + box.maxX) / 2, (box.minY + box.maxY) / 2, midZ, this.frame);
    const instance = this.next(category);
    instance.position.set(f.x, f.y, f.z);
    instance.rotation.y = f.yaw;
    instance.scaling.set(box.maxX - box.minX, box.maxY - box.minY, box.maxZ - box.minZ);
    instance.setEnabled(true);
  }

  end(): void {
    for (const category of Object.keys(this.pools) as Category[]) {
      const pool = this.pools[category];
      for (let i = this.used[category]; i < pool.length; i++) pool[i]?.setEnabled(false);
    }
  }

  private next(category: Category): InstancedLinesMesh {
    const pool = this.pools[category];
    const index = this.used[category]++;
    let instance = pool[index];
    if (!instance) {
      instance = this.sources[category].createInstance(`${this.sources[category].name}_${index}`);
      instance.isPickable = false;
      pool.push(instance);
    }
    return instance;
  }
}

function unitCube(scene: Scene, name: string, color: ReturnType<Color3['toColor4']>): LinesMesh {
  const h = 0.5;
  const c = [
    new Vector3(-h, -h, -h),
    new Vector3(h, -h, -h),
    new Vector3(h, -h, h),
    new Vector3(-h, -h, h),
    new Vector3(-h, h, -h),
    new Vector3(h, h, -h),
    new Vector3(h, h, h),
    new Vector3(-h, h, h),
  ] as const;
  const edges: [number, number][] = [
    [0, 1], [1, 2], [2, 3], [3, 0],
    [4, 5], [5, 6], [6, 7], [7, 4],
    [0, 4], [1, 5], [2, 6], [3, 7],
  ];
  const lines = edges.map(([a, b]) => [c[a] as Vector3, c[b] as Vector3]);
  const mesh = CreateLineSystem(name, { lines, colors: lines.map(() => [color, color]) }, scene);
  mesh.isVisible = false;
  mesh.isPickable = false;
  mesh.renderingGroupId = DEBUG_RENDERING_GROUP;
  return mesh;
}
